// Selector de ubicación con mapa Leaflet (modal). Click en el mapa coloca un
// marker draggable; "Usar esta ubicación" devuelve las coordenadas. Leaflet se
// carga de forma diferida solo al abrir el modal (mismo patrón que el mapa del
// dashboard y el de pagos), y el mapa se destruye al cerrar.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Typography, theme } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import type { DragEndEvent, LeafletMouseEvent } from 'leaflet';
import { useT } from '../locales';
import { useLeaflet } from '../lib/useLeaflet';
import { PIN_ANCHOR, PIN_SIZE, pinHtml } from '../lib/pin';

const { Text } = Typography;

// Centro de Guatemala, zoom urbano por defecto.
const CENTRO_DEFAULT: [number, number] = [14.6349, -90.5069];
const ZOOM_DEFAULT = 12;
const MAX_ZOOM_TILES = 19;
const ALTURA_MAPA = 420;

// Pin clásico de Leaflet (punta abajo) con el token primary: compartido con
// MapaPagosCliente y VerMapaButton vía src/lib/pin.ts.

export interface UbicacionCoords {
  lat: number;
  lng: number;
}

interface MapaUbicacionPickerProps {
  value?: { lat: number | null; lng: number | null } | null;
  onChange?: (v: UbicacionCoords | null) => void;
  disabled?: boolean;
}

function coordsValidas(value: MapaUbicacionPickerProps['value']): value is UbicacionCoords {
  return value != null && typeof value.lat === 'number' && typeof value.lng === 'number';
}

export function MapaUbicacionPicker({ value, onChange, disabled }: MapaUbicacionPickerProps) {
  const t = useT();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const L = useLeaflet(open);
  const [draft, setDraft] = useState<UbicacionCoords | null>(null);

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<import('leaflet').Map | null>(null);
  const markerRef = useRef<import('leaflet').Marker | null>(null);
  // Ref del draft para el efecto del mapa: colocar/arrastrar el marker no debe
  // recrear el mapa (setDraft solo alimenta el estado visible del footer).
  const draftRef = useRef<UbicacionCoords | null>(null);

  const tieneValor = coordsValidas(value);

  // Al abrir, el borrador arranca desde el value actual (marker inicial).
  useEffect(() => {
    if (!open) return;
    const inicial = coordsValidas(value) ? { lat: value.lat, lng: value.lng } : null;
    draftRef.current = inicial;
    setDraft(inicial);
  }, [open, value]);

  const colocarMarker = useCallback(
    (latlng: { lat: number; lng: number }) => {
      if (!L || !mapInstanceRef.current) return;
      draftRef.current = { lat: latlng.lat, lng: latlng.lng };
      setDraft(draftRef.current);

      markerRef.current?.remove();
      const icon = L.divIcon({
        className: '',
        html: pinHtml(token.colorPrimary),
        iconSize: PIN_SIZE,
        iconAnchor: PIN_ANCHOR,
      });
      const marker = L.marker([latlng.lat, latlng.lng], { draggable: true, icon });
      marker.on('dragend', (e: DragEndEvent) => {
        const ll = e.target.getLatLng();
        draftRef.current = { lat: ll.lat, lng: ll.lng };
        setDraft(draftRef.current);
      });
      marker.addTo(mapInstanceRef.current);
      markerRef.current = marker;
    },
    [L, token.colorPrimary],
  );

  // Con destroyOnHidden el contenido del modal se monta DESPUÉS de la animación
  // de apertura: el div del mapa puede no existir cuando corren los efectos del
  // padre (por eso el mapa no reaparecía al reabrir el modal). afterOpenChange
  // garantiza el momento exacto en que el contenido ya está montado; el efecto
  // [open, L] cubre el caso donde Leaflet aún no había terminado de cargar al
  // abrir (el guard de mapInstanceRef evita la doble inicialización).
  const sincronizarMapa = useCallback(
    (visible: boolean) => {
      if (!visible) {
        mapInstanceRef.current?.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        return;
      }
      if (!L || mapInstanceRef.current || !mapDivRef.current) return;

      const map = L.map(mapDivRef.current, {
        center: CENTRO_DEFAULT,
        zoom: ZOOM_DEFAULT,
        zoomControl: true,
        maxZoom: MAX_ZOOM_TILES,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: MAX_ZOOM_TILES,
      }).addTo(map);

      mapInstanceRef.current = map;

      const inicial = draftRef.current;
      if (inicial) {
        map.setView([inicial.lat, inicial.lng], ZOOM_DEFAULT);
        colocarMarker(inicial);
      }

      map.on('click', (e: LeafletMouseEvent) => {
        colocarMarker({ lat: e.latlng.lat, lng: e.latlng.lng });
      });
    },
    [L, colocarMarker],
  );

  useEffect(() => {
    if (open && L) sincronizarMapa(true);
    // sincronizarMapa cambia de identidad solo cuando Leaflet o el color
    // primario cambian; la sincronización real la dispara afterOpenChange del
    // Modal (el div del mapa recién existe cuando el contenido está montado).
  }, [open, L, sincronizarMapa]);

  const handleCerrar = () => {
    setOpen(false);
  };

  const handleLimpiar = () => {
    draftRef.current = null;
    setDraft(null);
    markerRef.current?.remove();
    markerRef.current = null;
  };

  const handleUsarUbicacion = () => {
    if (!draft) return;
    onChange?.({ lat: draft.lat, lng: draft.lng });
    setOpen(false);
  };

  const handleQuitar = () => {
    onChange?.(null);
  };

  const coordsTexto = tieneValor ? `${value.lat!.toFixed(5)}, ${value.lng!.toFixed(5)}` : '';

  // Tema oscuro para controles y atribución de Leaflet (por defecto son blancos).
  // Scoped al contenedor del mapa vía data-testid para no afectar otros mapas.
  const leafletCss = `
    [data-testid="mapa-ubicacion"] .leaflet-control-attribution {
      background: ${token.colorBgElevated}f2;
      color: ${token.colorTextTertiary};
      font-size: 10px;
      padding: 2px 10px;
      border-radius: 6px;
      margin: 0 10px 10px 0;
    }
    [data-testid="mapa-ubicacion"] .leaflet-control-attribution a {
      color: ${token.colorTextSecondary};
    }
    [data-testid="mapa-ubicacion"] .leaflet-bar a {
      background: ${token.colorBgElevated};
      color: ${token.colorText};
      border-bottom-color: ${token.colorBorderSecondary};
    }
    [data-testid="mapa-ubicacion"] .leaflet-bar a:hover {
      background: ${token.colorFillSecondary};
      color: ${token.colorText};
    }
  `;

  return (
    <>
      {tieneValor ? (
        <Space wrap>
          <Button icon={<EnvironmentOutlined />} disabled={disabled} onClick={() => setOpen(true)}>
            {coordsTexto}
          </Button>
          <Button disabled={disabled} onClick={handleQuitar}>
            {t('pages.servicios.quitarUbicacion')}
          </Button>
        </Space>
      ) : (
        <Button icon={<EnvironmentOutlined />} disabled={disabled} onClick={() => setOpen(true)}>
          {t('pages.servicios.seleccionarUbicacion')}
        </Button>
      )}
      <Modal
        title={t('pages.servicios.ubicacion')}
        open={open}
        onCancel={handleCerrar}
        afterOpenChange={sincronizarMapa}
        destroyOnHidden
        centered
        width={720}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {draft ? (
              <Button key="clear" onClick={handleLimpiar}>
                {t('common.clear')}
              </Button>
            ) : (
              <span />
            )}
            <Space>
              <Button key="cancel" onClick={handleCerrar}>
                {t('common.cancel')}
              </Button>
              <Button key="use" type="primary" disabled={!draft} onClick={handleUsarUbicacion}>
                {t('pages.servicios.usarUbicacion')}
              </Button>
            </Space>
          </div>
        }
      >
        <style>{leafletCss}</style>
        <div
          ref={mapDivRef}
          data-testid="mapa-ubicacion"
          style={{
            height: ALTURA_MAPA,
            width: '100%',
            borderRadius: 8,
            // Confina los panes internos de Leaflet (z-index 200-1000) al modal.
            isolation: 'isolate',
          }}
        />
        <div style={{ marginTop: 10, minHeight: 20 }}>
          {draft ? (
            <Space size={6}>
              <EnvironmentOutlined style={{ color: token.colorPrimary }} />
              <Text
                style={{ fontSize: 13 }}
              >{`${draft.lat.toFixed(5)}, ${draft.lng.toFixed(5)}`}</Text>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('pages.servicios.mapaHint')}
            </Text>
          )}
        </div>
      </Modal>
    </>
  );
}
